<!-- eslint-disable vue/no-v-html -->
<script setup lang="ts">
  import { cdnApi } from '@/helpers/api'
  import { date } from '@/helpers/converter'
  import { computedAsync } from '@vueuse/core'
  import { computed, ref, watch } from 'vue'
  import { useRoute, useRouter } from 'vue-router'
  const route = useRoute()
  const router = useRouter()
  const cdnApiPath = computed(
    () =>
      route.meta.cdnApiPath ??
      (route.name === 'general-post-detail' ? `/general-posts/${route.params.newsId}` : `/news/${route.params.newsId}`)
  )

  const isLoading = ref(false)

  interface NewsDetail {
    name: string
    body: string
    liveDate: string
    category: string
    summary: string
    imageThumbnail: string
  }

  const newsDetail = computedAsync(
    async (onCancel) => {
      const controller = new AbortController()
      onCancel(() => controller.abort())

      return cdnApi.get(cdnApiPath.value, { signal: controller.signal }).then(({ data }) => data as NewsDetail)
    },
    { name: '', body: '', liveDate: '', category: '', summary: '', imageThumbnail: '' },
    {
      evaluating: isLoading,
      onError: () => {
        router.replace({ name: 'news-list' })
      },
    }
  )
  const metaCurrentUrl = computed(() => window.location.href)
  const metaTitle = computed(() => newsDetail.value.name)
  const metaDescription = computed(() => newsDetail.value.summary)
  const metaImage = computed(() => newsDetail.value.imageThumbnail)

  useHead({
    meta: [
      { name: 'title', content: `MapleStory | ${metaTitle}` },
      { name: 'description', content: metaDescription },
      // Open Graph
      { property: 'og:title', content: metaTitle },
      { property: 'og:description', content: metaDescription },
      { property: 'og:type', content: 'article' },
      { property: 'og:url', content: metaCurrentUrl },
      { property: 'og:image', content: metaImage },
      // Twitter
      { name: 'twitter:title', content: metaTitle },
      { name: 'twitter:description', content: metaDescription },
      { name: 'twitter:url', content: metaCurrentUrl },
      { name: 'twitter:image', content: metaImage },
    ],
  })

  const isPatchNoteNews = computed(() => !!newsDetail.value.name.match(/v\.\d+/i))

  watch(newsDetail, () => {
    document.title = document.title + ' - ' + newsDetail.value.name
  })
</script>
<template>
  <CardWrapper>
    <Loading :show="isLoading">
      <div
        v-ga-section-view="{
          newsCategory: newsDetail.category,
          sectionContent: newsDetail.name,
        }"
        class="news-detail"
        data-section-name="Contents"
        data-section-number="2"
      >
        <div v-if="route.name === 'news-detail'" class="news-detail__little-info">
          <div class="news-detail__news-type" :class="newsDetail.category">{{ newsDetail.category }}</div>
          <div v-if="newsDetail.liveDate" class="news-detail__live-date">
            {{ date(newsDetail.liveDate) }}
          </div>
        </div>
        <h1 v-if="!route.meta.cdnApiPath" class="news-detail__title title xs">{{ newsDetail.name }}</h1>
        <CMSHtmlWrapper :value="newsDetail.body" />
        <ChatbotWindow v-if="isPatchNoteNews" :news-title="newsDetail.name" />
      </div>
    </Loading>
  </CardWrapper>
</template>

<style lang="scss" scoped>
  $newsType: (
    update: var(--news-update),
    events: var(--news-events),
    general: var(--news-general),
    community: var(--news-community),
    maintenance: var(--news-maintenance),
    sale: var(--news-sale),
  );

  .news-detail__little-info {
    display: flex;
    align-items: center;
    margin-bottom: 20px;
  }
  .news-detail__live-date {
    margin-left: 15px;
    font-size: 18px;
  }
  .news-detail__news-type {
    text-transform: uppercase;
    padding: 8px 10px;
    color: white;
    font-size: 18px;
    display: inline-block;
    @each $key, $val in $newsType {
      &.#{$key} {
        background: $val;
      }
    }
  }
  .news-detail__title {
    border-bottom: solid 2px var(--accent-news-color1);
    padding-bottom: 5px;
    margin-bottom: 30px;
  }

  @media #{$tablet} {
    .news-detail__title {
      font-size: 45px;
    }

    .news-detail__live-date,
    .news-detail__news-type {
      font-size: 16px;
    }

    .news-deail__news {
      :deep() {
        font-size: 14px;
      }
    }
  }

  @media #{$mobile} {
    .news-detail__title {
      font-size: 30px;
    }
  }
</style>
